// import the token here to test that our jest-preset handles it
import * as tokens from "@amzn/awsui-design-tokens";
import React from "react";
import {Box, ColumnLayout, Container, Grid, Header, Link, Popover, SpaceBetween} from '@amzn/awsui-components-react-v3';
import {AppRoutes, ButtonWithRouter, ExternalRoutes, RouteLabels} from "src/components/common/router-common";
import {
    DataStoreFields,
    FilterOperation,
    QueryVerb
} from "src/components/sm-overview-dashboards/workflow-list-view/filters/filter-constants";
import {getTimeRange} from "src/components/sm-overview-dashboards/workflow-detail-view/workflow-detail-view.utils";
import {getTimeDiff} from "src/utils/sm-overview-dashboard-utils";
import {QueryParams} from "src/components/sm-overview-dashboards/common/query-params";

// The content in the main content area of the App layout

export const MoreInfo: React.FC<{infoText: string, detail: string}> = ({infoText, detail}) => {
    return  <p style={{margin: '8px', display: 'inline-flex'}}>
        <Popover
            dismissButton={true}
            position="top"
            size="large"
            triggerType="custom"
            content={
            <div>
                <p>
                    {infoText}
                </p>
                <br/>
                <p>
                    {detail}
                </p>
            </div>
            }
        >
            <Link variant="info">Info</Link>
        </Popover>
    </p>
}

const getDashboardLinkWithDefaultParams = (link: string) => {
    const filterQueryParam = {
        [`${QueryVerb.MUST}::${FilterOperation.RANGE}:${DataStoreFields.WORKFLOW_START_TIME}`]
            : getTimeRange(getTimeDiff(3, 'MONTH'), 0)
    };
    return `${link}?${QueryParams.FILTER_QUERY_KEY}=${JSON.stringify(filterQueryParam)}`;
}

export default function HomepageContent() {
    return (
        <div>
            <div style={{background: tokens.colorBackgroundHomeHeader}} className={"custom-home__header"}>
                <Box padding={{vertical: "xxl", horizontal: "s"}}>
                    <Grid
                        gridDefinition={[
                            {colspan: {l: 8, xxs: 10}, offset: {l: 2, xxs: 1}},
                            {colspan: {l: 5, s: 6, xxs: 10}, offset: {l: 2, xxs: 1}},
                            {colspan: {l: 3, s: 4, xxs: 10}, offset: {s: 0, xxs: 1}},
                        ]}
                    >
                        <Box padding={{top: "xs"}} fontWeight="light">
                            <span className="custom-home-text-inverted">SMT</span>
                        </Box>
                        <div className="custom-home-text-inverted">
                            <Box variant="h1" fontWeight="bold" padding="n" fontSize="display-l" color="inherit">
                                SWO UI
                            </Box>
                            <Box fontWeight="light" padding={{bottom: "s"}} fontSize="display-l" color="inherit">
                                System Workflow Orchestrator
                            </Box>
                            <Box variant="p" fontWeight="light">
                                <span className="custom-home-text-secondary">
                  SWO translates a business intent of retrieving external selection information into a series of steps
                  involving SM systems and manual tasks
                </span>
                            </Box>
                        </div>
                    </Grid>
                </Box>
            </div>

            <Box margin={{vertical: 's'}} padding={{top: 'xl', horizontal: 'xl'}}>
                <Grid
                    gridDefinition={[
                        {colspan: {xl: 8, l: 8, s: 8, xxs: 10}, offset: {l: 2, xxs: 1}}
                    ]}
                >
                    <div className="custom-home-main-content-area">
                        <SpaceBetween size="l">
                            <div>
                                <Container>
                                    <Container
                                        header={
                                            <Header
                                                variant="h3"
                                            >
                                                SWO Assistant
                                            </Header>
                                        }
                                    >
                                        <div>
                                            <Box variant="p">
                                                SWO Agentic AI Assistant
                                                <MoreInfo
                                                    infoText={'SWO Agentic AI provides conversational interface for natural language querying of SWO data and workflow onboarding assistance.'}
                                                    detail={'Features SAGA for data analytics and onboarding guidance. Addresses slow turnaround times and reduces dependency on tribal knowledge by enabling self-service through AI agents that understand user intent and execute analytical tasks automatically.'}/>
                                            </Box>

                                            <Box variant="div" padding={{bottom: 'xs'}}>
                                                <ColumnLayout columns={0}>
                                                    <ButtonWithRouter
                                                        href={ExternalRoutes.swoAssistant}
                                                        text={'SWO agentic AI assistant'}
                                                        icon={"external"}
                                                        iconAlign={"right"}/>
                                                </ColumnLayout>
                                            </Box>

                                        </div>
                                    </Container>
                                    <Container
                                        header={
                                            <Header
                                                variant="h3"
                                            >
                                                Workflow Analysis
                                            </Header>
                                        }
                                    >
                                        <div>
                                            <Box variant="p">
                                                Swift Website Flow Analysis & Diagnostic Tool
                                                <MoreInfo
                                                    infoText={'SWIFT enables end users to analyse and track progress of website data flow across SM Primitives. This tool tracks website progress across Primitives irrespective of whether underlying Primitives process data at website grain or across websites, whether Primitives run on delta or snapshots or whether Primitives run on event based or scheduled triggers.'}
                                                    detail={'End users can configure and monitor Page0 website metrics like Product discovery to ASIN creation cycle time for ASIN first approach, TaT to surface selection insights etc. End users can also track which metrics are green and which are in alarm state based on thresholds and SLAs configured. End users can track website metadata like type of website, ecommerce platform etc, country of origin, operating countries, Programs, SubPrograms. End users can also track the treatment configured for the website across SM Pillars under the lens of respective Program and SubProgram, for example, anony, crawl completeness criteria, refresh rate in Selection Discovery; matching model used, is source qualified, deduper required in Selection Coverage & Insights pillar.'}/>
                                            </Box>

                                            <Box variant="div" padding={{bottom: 'xs'}}>
                                                <ColumnLayout columns={0}>
                                                    <ButtonWithRouter
                                                        href={ExternalRoutes.websiteFlowAnalysis}
                                                        text={'Swift Website Flow Analysis & Diagnostic Tool'}
                                                        icon={"external"}
                                                        iconAlign={"right"}/>
                                                </ColumnLayout>
                                            </Box>

                                        </div>
                                    </Container>
                                    <Container
                                       header={
                                            <Header
                                                variant="h3"
                                            >
                                                Workflow Actions
                                            </Header>
                                        }
                                    >
                                        <ColumnLayout borders="vertical"  columns={0} >
                                            <div>
                                                <Box variant="p" padding={{vertical: 'xs', horizontal: 'xs'}}>
                                                    This section exposes start(single and bulk), bulk-restart and bulk-redrive actions on the
                                                    workflow instances orchestrated by SWO orchestration engine.
                                                </Box>
                                                <Box variant="div" padding={{vertical: 'xs', horizontal: 'xs'}}>
                                                    <ButtonWithRouter href={AppRoutes.startWorkflowInstance}
                                                                      text={'Start new workflow instance'} icon={"external"}
                                                                      iconAlign={"right"}/>
                                                    <ButtonWithRouter
                                                        href={AppRoutes.restartWorkflowInstance}
                                                        text={'Bulk restart workflow instances'}
                                                        icon={"external"}
                                                        iconAlign={"right"}/>
                                                    <ButtonWithRouter
                                                        href={AppRoutes.redriveWorkflowInstance}
                                                        text={'Bulk redrive workflow instances'}
                                                        icon={"external"}
                                                        iconAlign={"right"}/>
                                                </Box>
                                            </div>
                                        </ColumnLayout>
                                    </Container>
                                    <Container
                                        header={
                                            <Header
                                                variant="h3"
                                            >
                                                Reporting
                                            </Header>
                                        }
                                    >
                                        <ColumnLayout borders="vertical" columns={0}>
                                            <div>
                                                <ColumnLayout borders="horizontal">
                                                    <div>
                                                        <Box variant="p">
                                                            Program/Sub-Program Health
                                                            <MoreInfo
                                                                infoText={'This section exposes view curated exclusively for programs and sub-programs.' +
                                                                    'Each view is designed to showcase key metrics associated with delivery of program/sub-program respectively'}
                                                                detail={''}
                                                            />
                                                        </Box>
                                                        <Box variant="div" padding={{bottom: 'xs'}}>
                                                            <ColumnLayout columns={0}>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.selectionGapClosureDashboard}
                                                                    text={'Selection Gap Closure Dashboard'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.dawnZumaDashboard}
                                                                    text={'Dawn-Zuma sub-program dashboard'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                            </ColumnLayout>
                                                        </Box>
                                                    </div>
                                                    <div>
                                                        <Box variant="p">
                                                            Capability/Primitive Health
                                                            <MoreInfo
                                                                infoText={'Gives a birds eye view of the key metrics, at Capability grain. Each workflow registered on SWO is mapped to Capability.' +
                                                                    ' Program data is delivered by stitching multiple workflows/capabilities.'}
                                                                detail={'This view is interactive and offers ability to user explore or drill-down the metrics and navigate to next layer (Page 1).\n' +
                                                                    '                                                    This First layer adds the 360 degree view with Aggregate Metrics. This view consolidates health of SM System.'}
                                                            />
                                                        </Box>
                                                        <Box variant="div" padding={{bottom: 'xs'}}>
                                                            <ColumnLayout columns={0}>
                                                                <ButtonWithRouter
                                                                    href={getDashboardLinkWithDefaultParams(AppRoutes.productSummary)}
                                                                    text={'Capability Health'} icon={"external"}
                                                                    iconAlign={"right"}/>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.failureAnalysis}
                                                                    text={'Capability failure analysis'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.workflowCycleTimeDeepDive}
                                                                    text={'Capability cycle time analysis'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.capabilityPrimitiveThroughput}
                                                                    text={'Throughput'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                            </ColumnLayout>
                                                        </Box>
                                                    </div>
                                                    <div>
                                                        <Box variant="p">
                                                            Operations Health
                                                            <MoreInfo
                                                                infoText={'This gives insights of Operator efficiency '}
                                                                detail={''}
                                                            />
                                                        </Box>
                                                        <Box variant="div" padding={{bottom: 'xs'}}>
                                                            <ColumnLayout columns={0}>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.rorDeepDiveWikiDashboard}
                                                                    text={'ROR dive deep'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                                <ButtonWithRouter
                                                                    href={ExternalRoutes.costPerWorkItem}
                                                                    text={'Cost per workitem'}
                                                                    icon={"external"}
                                                                    iconAlign={"right"}/>
                                                            </ColumnLayout>
                                                        </Box>
                                                    </div>
                                                    <div>
                                                        <Box variant="p">
                                                            Workflow Instance Analysis
                                                            <MoreInfo
                                                                infoText={'This view offers user a further dive deep to identify workflow instances.'}
                                                                detail={'This view enables user with an ability to Debug workflow instances\n' +
                                                                    ', using workflow properties to search and narrow down workflow instances.\n' +
                                                                    '       , and this also helps in self validation of dashboard, building trust in user about the data reported.'}/>
                                                        </Box>

                                                        <Box variant="div" padding={{bottom: 'xs'}}>
                                                            <ColumnLayout columns={0}>
                                                                <ButtonWithRouter
                                                                    href={getDashboardLinkWithDefaultParams(AppRoutes.workflowList)}
                                                                    text={'Workflow list'} icon={"external"}
                                                                    iconAlign={"right"}/>
                                                                <ButtonWithRouter href={AppRoutes.executionList}
                                                                                  text={'Executions view'}
                                                                                  icon={"external"}
                                                                                  iconAlign={"right"}/>
                                                            </ColumnLayout>
                                                        </Box>

                                                    </div>
                                                </ColumnLayout>
                                            </div>
                                        </ColumnLayout>
                                    </Container>
                                    <Container
                                        header={
                                            <Header
                                                variant="h3"
                                            >
                                                Program Source
                                            </Header>
                                        }
                                    >
                                        <div>
                                            <Box variant="p">
                                                Program Source Repository
                                                <MoreInfo
                                                    infoText={'This view offers users the ability to register, update, and get sources for programs in a self-serve manner.'}
                                                    detail={' It\'s a unified source of truth that dictates data delivery at a source, business (program) grain.' +
                                                        ' Program source repository is the glue which enables cross-primitive data orchestration across SM flywheel'}/>
                                            </Box>

                                            <Box variant="div" padding={{bottom: 'xs'}}>
                                                <ColumnLayout columns={0}>
                                                    <ButtonWithRouter href={AppRoutes.ProgramSourceConfigStore}
                                                                      text={'Program source repository'}
                                                                      icon={"external"}
                                                                      iconAlign={"right"}/>
                                                </ColumnLayout>
                                            </Box>

                                        </div>
                                    </Container>
                                </Container>
                            </div>
                        </SpaceBetween>
                    </div>
                </Grid>
            </Box>

        </div>
    );
}
